import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useStore } from '../../core/utils/hook';
import { canShowTrainingListMenu } from '../../core/utils/gaMenuPermissions';
import { MSG, SYSTEM_PATH } from '../../core/configs/constants';
import ProfileLayout from '../Profile/components/ProfileLayout';
import { ReactNotifications } from '../../components';

const MAX_CERTIFICATE_NAME_LENGTH = 150;

const CompletionSettingsScreen = observer(() => {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const ticketPlanName = location.state?.ticketPlanName || '';

    const {
        courseStore: { getCompletionSettingsForGA, updateCompletionSettingsForGA },
        userStore: { userInfoHeader, getUserInfoHeader }
    } = useStore();

    const [certificateName, setCertificateName] = useState('');
    const [courses, setCourses] = useState([]);
    const [checkedIds, setCheckedIds] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!userInfoHeader?.id) {
            getUserInfoHeader();
            return;
        }
        if (!canShowTrainingListMenu(userInfoHeader)) {
            navigate(SYSTEM_PATH.HOME, { replace: true });
            return;
        }
        if (!ticketId || isNaN(ticketId)) {
            navigate(SYSTEM_PATH.COMPANY_TRAINING_LIST);
            return;
        }
        fetchData();
    }, [ticketId, userInfoHeader?.id, userInfoHeader?.isAllowTrainingList]);

    const fetchData = async () => {
        const data = await getCompletionSettingsForGA(ticketId);
        if (data) {
            setCertificateName(data.completionCertificateName || '');
            setCourses(data.courses || []);
            setCheckedIds(
                (data.courses || []).filter(c => c.isChecked).map(c => c.id)
            );
        }
    };

    const handleToggleCourse = (id) => {
        setCheckedIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const handleSubmit = async () => {
        if (certificateName.length > MAX_CERTIFICATE_NAME_LENGTH) return;
        if (isSubmitting) return;

        setIsSubmitting(true);
        const payload = {
            completionCertificateName: certificateName || null,
            completionCourseIds: checkedIds
        };

        const ok = await updateCompletionSettingsForGA(ticketId, payload);
        if (ok) {
            ReactNotifications('success', MSG['inform.success.update']);
            navigate(SYSTEM_PATH.COMPANY_TRAINING_LIST + '?tab=ticket_plan');
        }
        setIsSubmitting(false);
    };

    const handleCancel = () => {
        navigate(SYSTEM_PATH.COMPANY_TRAINING_LIST + '?tab=ticket_plan');
    };

    return (
        <ProfileLayout titlePage='修了証設定'>
            <div className='detail-count-info-right completion-settings'>
                <div className='title-title'>
                    <h2 className='title-2'>修了証設定</h2>
                </div>

                <div className='completion-settings-content'>
                    <div className='content-student-detail'>
                        <div className='item'>
                            <div className='left'>
                                <label>受講プラン</label>
                            </div>
                            <div className='right'>
                                <span>{ticketPlanName}</span>
                            </div>
                        </div>

                        <div className='item'>
                            <div className='left'>
                                <label htmlFor='certificate-name'>修了証名</label>
                            </div>
                            <div className='right'>
                                <input
                                    id='certificate-name'
                                    type='text'
                                    className={`certificate-input${certificateName.length > MAX_CERTIFICATE_NAME_LENGTH ? ' input-error' : ''}`}
                                    value={certificateName}
                                    onChange={e => setCertificateName(e.target.value)}
                                    placeholder=''
                                />
                                {certificateName.length > MAX_CERTIFICATE_NAME_LENGTH && (
                                    <span className='error-message'>
                                        修了証名は{MAX_CERTIFICATE_NAME_LENGTH}文字以内で入力してください
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className='course-list-section'>
                        <div className='course-list-header'>
                            指定講座一覧 ({courses.length})
                        </div>
                        {courses.length > 0 ? (
                            <>
                                <p className='course-list-notice'>
                                    修了証の発行対象となる講座にチェックを入れてください
                                </p>
                                <div className='course-list'>
                                    {courses.map(course => (
                                        <label key={course.id} className='course-item'>
                                            <input
                                                type='checkbox'
                                                checked={checkedIds.includes(course.id)}
                                                onChange={() => handleToggleCourse(course.id)}
                                            />
                                            <span className='course-name'>{course.name}</span>
                                        </label>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className='no-courses'>講座はありません</div>
                        )}
                    </div>

                    <div className='completion-settings-footer'>
                        <button
                            type='button'
                            className='btn-cancel'
                            onClick={handleCancel}
                        >
                            キャンセル
                        </button>
                        <button
                            type='button'
                            className='btn-submit'
                            onClick={handleSubmit}
                            disabled={isSubmitting || certificateName.length > MAX_CERTIFICATE_NAME_LENGTH}
                        >
                            送信
                        </button>
                    </div>
                </div>
            </div>
        </ProfileLayout>
    );
});

export default CompletionSettingsScreen;
